import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, MessageSquareIcon, UserCheckIcon, CalendarIcon } from 'lucide-react';
export function Hero() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  const floatingCardVariants = {
    hidden: {
      x: 50,
      opacity: 0,
      scale: 0.9
    },
    visible: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 12
      }
    }
  };
  return <section className="relative bg-white overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32" data-id="element-443" data-mp="382p91mw8pvpw">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3" data-id="element-444" data-mp="3j9u0yz9q1olt">
        <div className="w-[600px] h-[600px] rounded-full bg-red-50 opacity-50 blur-3xl" data-id="element-445" data-mp="3o7vl7nivvtyv"></div>
      </div>
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3" data-id="element-446" data-mp="399bahlnnj9ux">
        <div className="w-[400px] h-[400px] rounded-full bg-gray-50 opacity-80 blur-3xl" data-id="element-447" data-mp="3e8wielksusjs"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-448" data-mp="30hrev8eb9g46">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" data-id="element-449" data-mp="358hmyte4fx2k">
          {/* Left Content */}
          <motion.div className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0" variants={containerVariants} initial="hidden" animate="visible" data-id="element-450" data-mp="2qkdcudt4wtve">
            <motion.div variants={itemVariants} className="mb-4" data-id="element-451" data-mp="2hix03zeukfo0">
              <span className="inline-block py-1 px-3 rounded-full bg-red-100 text-red-700 text-sm font-semibold tracking-wide" data-id="element-452" data-mp="2lkqz839e9bm2">
                Marketing Experts. Driven by Results.
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight" data-id="element-453" data-mp="nb1kz5gpvtwy">
              Not Hitting Your Marketing Goals?
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-454" data-mp="1b0vw9ile1af3">
              We are Marketing Experts at Driving & Turning Traffic into
              Conversions with a Focus on Building Customer Loyalty & Retention!
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-455" data-mp="x0l5efk2vvtx">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-md font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-456" data-mp="3hqtz20f4xpwf">
                SCHEDULE FREE SESSION
              </button>
              <button className="bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 px-8 py-4 rounded-md font-bold text-lg transition-all shadow-sm hover:shadow-md" data-id="element-457" data-mp="21bfy3xf9pty5">
                Learn More
              </button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center lg:justify-start space-x-4 text-sm text-gray-500" data-id="element-458" data-mp="2g0c3tntuquvl">
              <div className="flex -space-x-2" data-id="element-459" data-mp="2ozz3a430tqm0">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-460" data-mp="2tzbnsi6f6llf">
                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User avatar" className="w-full h-full object-cover" data-id="element-461" data-mp="zl11agdas3it" />
                  </div>)}
              </div>
              <div className="flex flex-col" data-id="element-462" data-mp="e9jxju49n9rw">
                <div className="flex items-center text-yellow-400" data-id="element-463" data-mp="e88vpzudsc3f">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-464" data-mp="9b8ohllsp3aq" />)}
                </div>
                <span data-id="element-465" data-mp="3ho69sjvny471">Trusted by 200+ businesses</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Device Mockup & Floating Cards */}
          <div className="lg:col-span-6 relative h-[500px] sm:h-[600px] flex items-center justify-center" data-id="element-466" data-mp="3rhen7m0gxvur">
            {/* Main Device Mockup (CSS only) */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="relative w-[280px] sm:w-[320px] h-[580px] sm:h-[640px] bg-gray-900 rounded-[3rem] border-[8px] border-gray-800 shadow-2xl overflow-hidden z-10" data-id="element-467" data-mp="2yyzlmhc3okdu">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-6 bg-gray-800 rounded-b-3xl w-40 mx-auto z-20" data-id="element-468" data-mp="14k3vhxv0z95o"></div>

              {/* Screen Content */}
              <div className="absolute inset-0 bg-gray-50 pt-12 px-4 pb-6 flex flex-col" data-id="element-469" data-mp="1x7zrf0crsx1e">
                <div className="flex justify-between items-center mb-6" data-id="element-470" data-mp="1j8gtt1hl543c">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center" data-id="element-471" data-mp="1j870b3xc0esw">
                    <span className="text-red-600 font-bold text-xs" data-id="element-472" data-mp="1nc54ht1ikfst">ad</span>
                  </div>
                  <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-473" data-mp="7vegcfas8une"></div>
                </div>

                <div className="space-y-4 flex-1" data-id="element-474" data-mp="bsskhsjlwc8j">
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-475" data-mp="2ehtxuclaxs79">
                    <div className="w-1/2 h-4 bg-gray-200 rounded mb-4" data-id="element-476" data-mp="29jil9u3mh3lc"></div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-477" data-mp="24r900rdgcrpe"></div>
                    <div className="w-3/4 h-2 bg-gray-100 rounded" data-id="element-478" data-mp="1zsnzequag60f"></div>
                  </div>
                  <div className="w-full h-24 bg-red-50 rounded-xl border border-red-100 p-4" data-id="element-479" data-mp="1zqtlvxlcjg6d">
                    <div className="w-1/3 h-4 bg-red-200 rounded mb-3" data-id="element-480" data-mp="1voq12icsez8f"></div>
                    <div className="w-full h-8 bg-white rounded-md" data-id="element-481" data-mp="1qqofnqrw2nts"></div>
                  </div>
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-482" data-mp="1lt544ymksp5j">
                    <div className="flex space-x-3 mb-4" data-id="element-483" data-mp="1gvmv788k69xj">
                      <div className="w-10 h-10 rounded-full bg-gray-200" data-id="element-484" data-mp="3g75yb676knza"></div>
                      <div className="flex-1" data-id="element-485" data-mp="3b82xjxy8h3x4">
                        <div className="w-1/2 h-3 bg-gray-200 rounded mb-2" data-id="element-486" data-mp="3b8cphr2u9l5g"></div>
                        <div className="w-1/3 h-2 bg-gray-100 rounded" data-id="element-487" data-mp="3jo25um4298wu"></div>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-488" data-mp="3nocanvg49ta4"></div>
                    <div className="w-5/6 h-2 bg-gray-100 rounded" data-id="element-489" data-mp="3shoza5ksq02v"></div>
                  </div>
                </div>

                {/* Bottom Nav */}
                <div className="h-14 bg-white rounded-2xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex justify-around items-center px-2 mt-auto" data-id="element-490" data-mp="30q93pm19h30i">
                  <div className="w-8 h-8 rounded-full bg-red-100" data-id="element-491" data-mp="34s2yxesoteta"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-492" data-mp="39qe77l3c3h9e"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-493" data-mp="3eph6eowmut9g"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-494" data-mp="3ennyz7e7450e"></div>
                </div>
              </div>
            </motion.div>

            {/* Floating Cards */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="absolute inset-0 z-20 pointer-events-none" data-id="element-495" data-mp="txyk4udhsj7p">
              {/* Card 1: New Lead */}
              <motion.div variants={floatingCardVariants} className="absolute top-[15%] -left-4 sm:-left-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-2deg]" data-id="element-496" data-mp="1ot3r0cpts7hh">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0" data-id="element-497" data-mp="1yte4zzjopixp">
                  <MessageSquareIcon className="w-5 h-5 text-green-600" data-id="element-498" data-mp="1ts6v9uxze9oe" />
                </div>
                <div data-id="element-499" data-mp="27rpsvtt622mg">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-500" data-mp="27pvhb63sbmv1">
                    New Lead
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-501" data-mp="df9n0u4a8qi4">
                    Sarah M.
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-502" data-mp="8golmrdao0s3">
                    Requested a quote for SEO services
                  </p>
                </div>
              </motion.div>

              {/* Card 2: Review */}
              <motion.div variants={floatingCardVariants} className="absolute top-[40%] -right-8 sm:-right-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-60 sm:w-64 rotate-[3deg]" data-id="element-503" data-mp="2p3eiaetesftr">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0" data-id="element-504" data-mp="21ao7wa15m4ll">
                  <StarIcon className="w-5 h-5 text-blue-600" data-id="element-505" data-mp="26c3qzwjru11c" />
                </div>
                <div data-id="element-506" data-mp="1sc2tmr2ptbqm">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-507" data-mp="1scm9m28t1q20">
                    Google Review
                  </p>
                  <div className="flex text-yellow-400 mb-1" data-id="element-508" data-mp="3mpllpldz824j">
                    {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-3 h-3 fill-current" data-id="element-509" data-mp="3qrfj9ksvkq0h" />)}
                  </div>
                  <p className="text-xs text-gray-500 italic" data-id="element-510" data-mp="b2u0psgpghm3">
                    "Amazing results in just 2 months!"
                  </p>
                </div>
              </motion.div>

              {/* Card 3: Conversion */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[30%] -left-8 sm:-left-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-1deg]" data-id="element-511" data-mp="17ij92dwwwan">
                <div className="bg-red-100 p-2 rounded-lg flex-shrink-0" data-id="element-512" data-mp="ys4sikg2v1o3">
                  <UserCheckIcon className="w-5 h-5 text-red-600" data-id="element-513" data-mp="kug81dpx9wjk" />
                </div>
                <div data-id="element-514" data-mp="ouqpbmj609dq">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-515" data-mp="ou8ijvyvmcrk">
                    New Conversion
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-516" data-mp="39liti31jlpk8">
                    Form Submitted
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-517" data-mp="3216vk8j6s0if">
                    Campaign: Local PPC Q3
                  </p>
                </div>
              </motion.div>

              {/* Card 4: Appointment */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[10%] -right-4 sm:-right-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-56 sm:w-64 rotate-[2deg]" data-id="element-518" data-mp="3bz4h2o9nvj7c">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0" data-id="element-519" data-mp="2ecxd9gl6luw1">
                  <CalendarIcon className="w-5 h-5 text-purple-600" data-id="element-520" data-mp="2t1bg3rilljcl" />
                </div>
                <div data-id="element-521" data-mp="2t34pheklihoa">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-522" data-mp="2oa1qus0yoi18">
                    Appointment
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-523" data-mp="20j63q87gab0b">
                    Ashley C.
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-524" data-mp="1vk37nd9g7f4h">
                    Strategy Session @ 2PM
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}